import type { FC } from 'react';
import classNames from 'classnames';
import { Button, Tag } from '@carbon/react';
import {
  Checkmark,
  CheckmarkFilled,
  CheckmarkOutline,
  CloseFilled,
  CloseOutline,
  Rocket,
  TaskView,
} from '@carbon/react/icons';
import { SidebarToggle } from 'components/layout/dual-sidebar-layout';
import { EventsPanelOpenButton } from 'components/events-panel';
import type {
  InventoryTaskStatus,
  InventoryWorkspaceResponse,
} from 'types/api-responses';

// The Type Curve workspace header's stylesheet, reused wholesale so both
// workspace kinds share the same dark bar. Only the wiring differs: that
// component reads TC-workspace task atoms, this one reads the inventory
// payload's own rollup and HATEOAS gates.
import style from 'components/type-curve-workspace/type-curve-workspace-header.module.scss';
import { WorkspaceHeaderLeading } from 'components/type-curve-workspace';

interface InventoryWorkspaceHeaderProps {
  workspace: InventoryWorkspaceResponse;
  /** The launched Type Curve's label, shown as the subtitle. */
  typeCurveLabel: string | null;
  leftPanelOpen: boolean;
  onLeftPanelToggle: (open: boolean) => void;
  tasksPanelOpen: boolean;
  onToggleTasks: () => void;
  /** Checklist verdict rollup (the `inventory_workspace` gate is excluded). */
  taskCompleted: number;
  taskTotal: number;
  /** The `inventory_workspace` gate task's current verdict. */
  gateStatus: InventoryTaskStatus;
  /** Clicking a verdict opens the decision panel (or reverts if already active);
   *  the page owns that logic. */
  onMarkCorrect: () => void;
  onMarkIncorrect: () => void;
  gateSaving: boolean;
  /** Promotable — every required verification task + the gate carry a verdict,
   *  and the workspace is not already promoted. Computed by the page. */
  canPromote: boolean;
  onPromote: () => void;
  onBack: () => void;
}

// Workspace bar: identity on the left, verification rollup + promote on the
// right. Promote enables only when `canPromote` (every required verification
// task + the workspace gate carry a verdict, computed by the page) — mirroring
// the Type Curve workspace. The server still has the final say: it answers 422
// NOT_PROMOTABLE with the blocking units, which the modal surfaces.
export const InventoryWorkspaceHeader: FC<InventoryWorkspaceHeaderProps> = ({
  workspace,
  typeCurveLabel,
  leftPanelOpen,
  onLeftPanelToggle,
  tasksPanelOpen,
  onToggleTasks,
  taskCompleted,
  taskTotal,
  gateStatus,
  onMarkCorrect,
  onMarkIncorrect,
  gateSaving,
  canPromote,
  onPromote,
  onBack,
}) => {
  const PromoteIcon = workspace.promoted ? Checkmark : Rocket;
  const gateIsCorrect = gateStatus === 'correct';
  const gateIsIncorrect = gateStatus === 'incorrect';

  return (
    <header className={style.shell}>
      <div className={style.row}>
        <WorkspaceHeaderLeading name={workspace.name} onBack={onBack}>
          <Tag type="teal" size="sm">
            Inventory Workspace
          </Tag>
          {workspace.source && workspace.source !== 'user' ? (
            <Tag type="purple" size="sm">
              {workspace.source}
            </Tag>
          ) : null}
          {typeCurveLabel ? (
            <span className={style.typeCurveId}>{typeCurveLabel}</span>
          ) : null}
        </WorkspaceHeaderLeading>

        <div className={style.actions}>
          <div className={style.darkToolbarControl}>
            <SidebarToggle
              side="left"
              isOpen={leftPanelOpen}
              onToggle={onLeftPanelToggle}
              label="Panel"
            />
          </div>
          {/* Activity feed + comment thread — the same shared panel the Type
              Curve workspace opens; self-wired via isEventsPanelOpenAtom. */}
          <div className={style.darkToolbarControl}>
            <EventsPanelOpenButton />
          </div>
          <button
            type="button"
            className={classNames(style.taskButton, {
              [style.taskButtonOpen]: tasksPanelOpen,
            })}
            aria-pressed={tasksPanelOpen}
            onClick={onToggleTasks}
          >
            <span className={style.taskLabel}>
              Tasks {taskCompleted}/{taskTotal}
            </span>
            <TaskView size={16} className={style.taskIcon} aria-hidden />
          </button>
          {/* The workspace-level gate verdict (`inventory_workspace` path).
              Hidden once promoted, when verdicts are locked. Clicking an
              inactive verdict opens the decision panel to capture a reason;
              re-clicking the active verdict reverts to unverified. */}
          {!workspace.promoted ? (
            <>
              <button
                type="button"
                className={style.markButton}
                disabled={gateSaving}
                onClick={onMarkCorrect}
              >
                <span className={style.markButtonLabel}>Mark as Correct</span>
                {gateIsCorrect ? (
                  <CheckmarkFilled
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                ) : (
                  <CheckmarkOutline
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                )}
              </button>
              <button
                type="button"
                className={style.markButton}
                disabled={gateSaving}
                onClick={onMarkIncorrect}
              >
                <span className={style.markButtonLabel}>Mark as Incorrect</span>
                {gateIsIncorrect ? (
                  <CloseFilled
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                ) : (
                  <CloseOutline
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                )}
              </button>
            </>
          ) : null}
          <Button
            kind="primary"
            size="md"
            disabled={!canPromote}
            className={style.promoteButton}
            onClick={onPromote}
            renderIcon={(props) => <PromoteIcon size={16} {...props} />}
          >
            {workspace.promoted ? 'Promoted' : 'Promote'}
          </Button>
        </div>
      </div>
    </header>
  );
};
